import type { Timing } from "@patient-watch/types";

/** Approximate milliseconds per unit for sorting occurrences across mixed units. */
const UNIT_MS: Record<string, number> = {
    second: 1000,
    minute: 60_000,
    hour: 3_600_000,
    day: 86_400_000,
    week: 7 * 86_400_000,
    month: 30 * 86_400_000,
    year: 365 * 86_400_000,
};

function timeUnitSuffix(timeUnit: string): string {
    switch (timeUnit) {
        case "second":
            return "s";
        case "minute":
            return "min";
        case "hour":
            return "h";
        case "day":
            return "d";
        case "week":
            return "w";
        case "month":
            return "m";
        case "year":
            return "y";
        default:
            return timeUnit.slice(0, 2);
    }
}

function compactOccurrenceLabel(value: number, timeUnit: string): string {
    if (value === 0) return "Baseline";

    if (timeUnit === "week") {
        if (value % 52 === 0) return `${value / 52}y`;
        if (value % 4 === 0) return `${value / 4}m`;
    }

    if (timeUnit === "month" && value % 12 === 0) {
        return `${value / 12}y`;
    }

    return `${value}${timeUnitSuffix(timeUnit)}`;
}

function sortTimingsChronologically(timings: Timing["Row"][]): Timing["Row"][] {
    return [...timings].sort((a, b) => {
        const ua = UNIT_MS[a.time_unit] ?? 0;
        const ub = UNIT_MS[b.time_unit] ?? 0;
        return a.interval * ua - b.interval * ub;
    });
}

function occurrenceMs(timing: Timing["Row"], indexZeroBased: number): number {
    const unit = UNIT_MS[timing.time_unit] ?? 0;
    return timing.interval * (indexZeroBased + 1) * unit;
}

function occurrenceLabel(timing: Timing["Row"], indexZeroBased: number): string {
    if (!timing.is_autoname && timing.label) return timing.label;
    const n = timing.interval * (indexZeroBased + 1);
    return compactOccurrenceLabel(n, timing.time_unit);
}

function joinWithMiddleEllipsis(labels: string[], headCount: number, tailCount: number): string {
    if (labels.length <= headCount + tailCount) {
        return labels.join(", ");
    }
    const head = labels.slice(0, headCount).join(", ");
    const tail = labels.slice(-tailCount).join(", ");
    return `${head}, …, ${tail}`;
}

export type ScheduleLike = { timings?: Timing["Row"][]; schedule_name?: string | null } | null | undefined;

/**
 * Short, scannable schedule label for pickers and lists (e.g. `Baseline, 2w, 3m, 6m, 1y`).
 * Uses a flat list of timepoints rather than cadence segments (`weekly to 1y`).
 */
export function formatScheduleSummary(schedule: ScheduleLike): string {
    return formatScheduleTimepointsList(schedule);
}

/**
 * Flatten schedule timings into one chronological list of compact labels (e.g. `Baseline, 1w, 2w, …, 52w`).
 * Long lists are shortened as `1d, 2d, 1w, 2w, …, 19w, 20w`. Infinite timings contribute their first
 * three points, then ", …" is appended to the overall string.
 */
export function formatScheduleTimepointsList(schedule: ScheduleLike): string {
    const timings = schedule?.timings;
    if (!timings?.length) {
        return schedule?.schedule_name?.trim() ?? "";
    }

    const sortedTimings = sortTimingsChronologically(timings);
    const items: { ms: number; label: string }[] = [];
    let hasInfinite = false;

    for (const timing of sortedTimings) {
        if (timing.is_infinite) {
            hasInfinite = true;
            for (let i = 0; i < 3; i++) {
                items.push({
                    ms: occurrenceMs(timing, i),
                    label: occurrenceLabel(timing, i),
                });
            }
            continue;
        }

        const repeats = timing.repeats ?? 0;
        const occurrenceCount = repeats > 0 ? repeats + 1 : 1;
        for (let i = 0; i < occurrenceCount; i++) {
            items.push({
                ms: occurrenceMs(timing, i),
                label: occurrenceLabel(timing, i),
            });
        }
    }

    items.sort((a, b) => (a.ms === b.ms ? a.label.localeCompare(b.label) : a.ms - b.ms));

    const labels = items.map((x) => x.label);
    let result = joinWithMiddleEllipsis(labels, 4, 2);

    if (hasInfinite) {
        result = result ? `${result}, …` : "…";
    }

    return result;
}
